import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpInterceptorFn,
} from '@angular/common/http';
import { Observable, catchError, of, map, finalize, throwError } from 'rxjs';
import { CommonMethods, Severity } from '../shared/helpers';
import { AuthenticationService, SpinnerService } from '../shared/services';

export const commonInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<any> => {
  const commonMethods = inject(CommonMethods);
  const authService = inject(AuthenticationService);
  const spinnerService = inject(SpinnerService);

  const skipSpinner = req.headers.has('X-Skip-Spinner');
  if (!skipSpinner) spinnerService.show();

  // Strip the custom header so it is never sent to the backend
  if (skipSpinner) {
    req = req.clone({ headers: req.headers.delete('X-Skip-Spinner') });
  }

  let currentUser = localStorage.getItem('currentBIUser');
  let authReq = req;
  const token = (currentUser !== 'undefined') ? JSON.parse(currentUser || '{}').auth_token : '';
  let urls = req.url.split('/');
  const configJsonFiles = ['env.json', 'development.json', 'staging.json', 'preprod.json', 'cloudrun.json', 'production.json'];
  if (token && !configJsonFiles.includes(req.url.split('/').pop()!) && !['generate-token'].includes(urls[4])) {
    if (['uploadDocument'].includes(req.url.split('/').pop()!)) {
      authReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`)
      });
    } else {
      authReq = req.clone({
        headers: req.headers.set('Content-type', 'application/json')
          .set('Authorization', `Bearer ${token}`)
      });
    }
  }

  const isSessionHeartbeat = authReq.url.includes('/auth/session/heartbeat');

  return next(authReq).pipe(
    map((res: any) => {
      // Heartbeat owns its own idle UX — do not toast or force-logout on status 0/1.
      if (isSessionHeartbeat) {
        return res;
      }
      if (!configJsonFiles.includes(authReq.url.split('/').pop()!)) {
        if (res.body?.status === 1) {
          if (res.body?.message) {
            commonMethods.clearToast();
            commonMethods.showToastMessage(res.body.message, 'Success', Severity.success);
          }
        } else if (res.body?.status === 0) {
          if (res.body?.message) {
            commonMethods.clearToast();
            commonMethods.showToastMessage(res.body.message, 'Error', Severity.error);
          }
        } else if (res.body?.status === 2) {
          if (res.body?.message) {
            commonMethods.clearToast();
            commonMethods.showToastMessage(res.body.message, 'Error', Severity.error);
          }
          authService.sessionExpiry();
        } else if (res.body?.status === 3) {
          if (res.body?.message) {
            commonMethods.clearToast();
            commonMethods.showToastMessage(res.body.message, 'Error', Severity.error);
          }
          authService.sessionExpiry();
        }
      }
      return res;
    }),
    catchError((res: any) => {
      if (isSessionHeartbeat) {
        return throwError(() => res);
      }
      commonMethods.clearToast();
      let errorMessage = `${res.error.message}`;
      console.log(`${res.status} - ${res.statusText} : ${res.message}`);
      // Handle HTTP 401 Unauthorized status code
      if (res.status === 401) {
        authService.handleUnauthorizedSession();
        return of(res);
      }

      if (res.error?.status === 0) {
        if (res.error?.message) {
          commonMethods.clearToast();
          commonMethods.showToastMessage(res.error.message, 'Error', Severity.error);
        }
      } else if (res.error?.status === 2) {
        if (res.error?.message) {
          commonMethods.clearToast();
          commonMethods.showToastMessage(res.error.message, 'Error', Severity.error);
        }
        authService.sessionExpiry();
      } else if (res.error.status === 3) {
        if (res.error.message) {
          commonMethods.clearToast();
          commonMethods.showToastMessage(res.error.message, 'Error', Severity.error);
        }
        authService.sessionExpiry();
      } else {
        commonMethods.showToastMessage(errorMessage, 'Error', Severity.error);
      }
      return of(res);
    }),
    finalize(() => {
      if (!skipSpinner) spinnerService.hide();
    })
  );
};
